import React, { useEffect, useRef } from 'react';
import KrManagerNav from '../../../components/KrManagerNav';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencilAlt, faHome } from '@fortawesome/free-solid-svg-icons';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { User, UserProfileInput } from '../../../interfaces/User';
import { useForm } from '../../../hooks/useForm';
import { emptyValidator } from '../../../utils/validators/emptyValidator';
import { nameValidator } from '../../../utils/validators/nameVaildator';
import userService from '../../../services/UserService';
import useApi from '../../../hooks/useApi';
import { BvTokens } from '../../../interfaces/Auth';
import { useAuthActions } from '../../../hooks/useReduxActions';
import defaultImg from '../../../assets/images/default.jpeg';
import { toast } from 'react-toastify';

const update_profile = (data: { tokens: BvTokens; data: UserProfileInput }) =>
	userService.update_profile(data.tokens, data.data);

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB

export const EditProfile: React.FC = () => {
	const avatarSelectRef = useRef<HTMLInputElement | null>(null);
	const inputRef = useRef<HTMLInputElement | null>(null);

	const { user_data, tokens } = useAppSelector((state) => state.authReducer);
	const { updateUserData } = useAuthActions();

	const profileForm = useForm<UserProfileInput>(
		{
			address: '',
			city: '',
			country: '',
			firstname: '',
			lastname: '',
			gender: '',
			phone: '',
			state: '',
		},
		{
			address: emptyValidator,
			city: emptyValidator,
			country: emptyValidator,
			firstname: nameValidator,
			lastname: nameValidator,
			gender: emptyValidator,
			state: emptyValidator,
		}
	);

	const profileApiRequest = useApi<
		{ status: boolean; data: User },
		{ tokens: BvTokens; data: UserProfileInput }
	>(update_profile);

	useEffect(() => {
		if (user_data) {
			profileForm.onChange('address', user_data.address);
			profileForm.onChange('firstname', user_data.firstname);
			profileForm.onChange('lastname', user_data.lastname);
			profileForm.onChange('city', user_data.city);
			profileForm.onChange('state', user_data.state);
			profileForm.onChange('phone', user_data.phone);
			profileForm.onChange('country', user_data.country);
			profileForm.onChange('gender', user_data.gender);
		}
	}, [user_data]);

	const handleChange: React.ChangeEventHandler<
		HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
	> = (e) => {
		profileForm.onChange(
			e.target.name as keyof UserProfileInput,
			e.target.value
		);
	};

	const handleFileSelected: React.ChangeEventHandler<HTMLInputElement> = (
		e
	) => {
		if (e.target.files) {
			const file = e.target.files[0];

			if (file.size > MAX_FILE_SIZE) {
				toast('File size too large. Maximum size is 2MB.', {
					type: 'error',
					theme: 'colored',
				});
				return;
			}

			profileForm.onChange('avatar', e.target.files[0]);
		}
	};

	function copyReferralCode() {
		if (!inputRef.current) {
			return;
		}

		inputRef.current.select();
		inputRef.current.setSelectionRange(0, 99999);

		navigator.clipboard.writeText(inputRef.current.value);

		toast('Referral code copied to clipboard!', {
			type: 'success',
			theme: 'colored',
		});
	}

	const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
		e.preventDefault();

		profileForm.resetFormErrors();
		profileApiRequest.reset();

		const valid = profileForm.validate();

		if (valid && tokens) {
			const result = await profileApiRequest.request({
				data: { ...profileForm.form },
				tokens,
			});

			if (result && result.status) {
				updateUserData(result.data);
				toast('Profile updated successfully!', {
					type: 'success',
					theme: 'colored',
				});
			}
		}
	};

	return (
		<div className='bv-edit-profile-page py-3 p-md-0'>
			<KrManagerNav />

			<div className='kr-manager-filter'>
				<button
					type='button'
					className='btn btn-autowidth btn-red btn-large'
					name='button'
				>
					Edit Profile
				</button>
			</div>

			<div className='container user-info-card mb-3'>
				<div className='card'>
					<div className='card-body d-flex align-items-center'>
						<div className='user-profile me-3'>
							<img
								src={
									profileForm.form.avatar
										? URL.createObjectURL(profileForm.form.avatar)
										: user_data?.avatar
										? encodeURI(user_data.avatar)
										: defaultImg
								}
								alt='avatar'
							/>

							<input
								className='form-control'
								type='file'
								ref={avatarSelectRef}
								onChange={handleFileSelected}
								style={{ display: 'none' }}
								accept='image/*'
							/>

							<FontAwesomeIcon
								className='icon cursor-pointer'
								icon={faPencilAlt}
								onClick={() => avatarSelectRef.current?.click()}
							/>
						</div>

						<div className='user-info'>
							<div className='d-flex align-items-center'>
								<h5 className='mb-1'>{user_data?.email}</h5>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className='kr-admin-table'>
				<form onSubmit={handleSubmit}>
					<div className='kr-admin-line kr-admin-line-cls'>
						<div className='kr-admin-field'>
							<div>
								<label>Referral Code</label>
							</div>
							<div>
								<input
									type='text'
									id='referralInput'
									ref={inputRef}
									readOnly
									name='reference'
									value={user_data?.referral_code || ''}
									className='form-control'
								/>
								<button
									type='button'
									className='btn btn-danger-light'
									onClick={copyReferralCode}
								>
									Copy Referral Code
								</button>
							</div>
						</div>

						<div className='kr-admin-field'>
							<div>
								<label>First Name</label>
							</div>
							<div>
								<input
									type='text'
									name='firstname'
									value={profileForm.form.firstname}
									onChange={handleChange}
									required
									placeholder='First Name'
									className='form-control'
								/>
							</div>
						</div>

						<div className='kr-admin-field'>
							<div>
								<label>Last Name </label>
							</div>
							<div>
								<input
									type='text'
									name='lastname'
									value={profileForm.form.lastname}
									onChange={handleChange}
									required
									placeholder='Last Name'
									className='form-control'
								/>
							</div>
						</div>

						<div className='kr-admin-field'>
							<div>
								<label>Email Address</label>
							</div>
							<div>
								<input
									type='text'
									name='email'
									value={user_data?.email || ''}
									readOnly
									className='form-control'
								/>
							</div>
						</div>

						<div className='kr-admin-field'>
							<div>
								<label>Phone</label>
							</div>
							<div>
								<input
									type='tel'
									name='phone'
									value={profileForm.form.phone || ''}
									onChange={handleChange}
									placeholder='Phone'
									className='form-control'
								/>
							</div>
						</div>

						<div className='kr-admin-field'>
							<div>
								<label>Gender</label>
							</div>
							<div>
								<select
									name='gender'
									className='form-control'
									value={profileForm.form.gender || ''}
									onChange={handleChange}
								>
									<option value='' disabled>
										Select Gender
									</option>
									<option value='male'>Male</option>
									<option value='female'>Female</option>
								</select>
							</div>
						</div>
					</div>

					<div className='kr-admin-line kr-admin-line-cls'>
						<div className='kr-admin-field'>
							<div>
								<label>Country</label>
							</div>
							<div>
								<select
									id='country'
									name='country'
									required
									value={profileForm.form.country || ''}
									onChange={handleChange}
									className='form-control'
								>
									<option value='' disabled>
										Select Country
									</option>
									<option value='United States of America'>
										United States of America
									</option>
									<option value='Afghanistan'>Afghanistan</option>
									<option value='Albania'>Albania</option>
									<option value='Algeria'>Algeria</option>
									{/* ... other country options ... */}
								</select>
							</div>
						</div>

						<div className='kr-admin-field'>
							<div>
								<label> State/region </label>
							</div>
							<div>
								<input
									type='text'
									name='state'
									value={profileForm.form.state || ''}
									onChange={handleChange}
									required
									placeholder='State/Region'
									className='form-control'
								/>
							</div>
						</div>

						<div className='kr-admin-field'>
							<div>
								<label>City</label>
							</div>
							<div>
								<input
									type='text'
									name='city'
									value={profileForm.form.city || ''}
									onChange={handleChange}
									required
									placeholder='City'
									className='form-control'
								/>
							</div>
						</div>

						<div className='kr-admin-field'>
							<div>
								<label>Address</label>
							</div>
							<div>
								<input
									type='text'
									name='address'
									value={profileForm.form.address || ''}
									onChange={handleChange}
									required
									placeholder='Address'
									className='form-control'
								/>
							</div>
						</div>
					</div>

					{profileApiRequest.error && (
						<div className='mt-3 api-error alert alert-danger text-center'>
							{profileApiRequest.error}
						</div>
					)}

					<div className='kr-admin-action'>
						<button
							type='submit'
							disabled={profileApiRequest.loading}
							className='btn btn-orange'
						>
							{profileApiRequest.loading ? 'Updating...' : 'Update Profile'}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
};
